import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Storefront, LockKey, CircleNotch, ArrowLeft } from "@phosphor-icons/react";
import { useDealerAuth } from "@/context/DealerAuthContext";
import { getErrorMessage } from "@/lib/api";
import { OtmLogo } from "@/components/common/OtmLogo";

export default function DealerLogin() {
  const { isAuthed, login } = useDealerAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(params.get("expired") ? "Your session expired. Please log in again." : "");

  useEffect(() => {
    if (isAuthed) navigate("/dealer/board", { replace: true });
  }, [isAuthed, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password) return;
    setLoading(true);
    setError("");
    try {
      await login(username, password);
      navigate("/dealer/board", { replace: true });
    } catch (err) {
      setError(getErrorMessage(err, "Incorrect username or password"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#050505] text-neutral-100 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <OtmLogo imgClassName="h-9" />
        </div>
        <form
          onSubmit={submit}
          data-testid="dealer-login-form"
          className="bg-[#0F0F0F] border border-neutral-800 rounded-2xl p-8 shadow-2xl"
        >
          <div className="flex items-center gap-2.5 mb-1">
            <div className="w-9 h-9 rounded-lg bg-[#73bf44]/10 flex items-center justify-center">
              <Storefront size={20} className="text-[#73bf44]" weight="duotone" />
            </div>
            <h1 className="font-heading text-xl font-bold text-white">Dealer Portal</h1>
          </div>
          <p className="text-sm text-neutral-500 mb-6">Sign in to view your warranty claims.</p>

          <label className="text-sm text-neutral-400 mb-1.5 block">Username</label>
          <input
            data-testid="dealer-username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            placeholder="e.g. 4krv"
            className="w-full bg-black/40 border border-neutral-700 rounded-lg px-3 py-2.5 text-sm text-white outline-none focus:border-[#73bf44] transition-colors placeholder:text-neutral-600 mb-4"
          />

          <label className="text-sm text-neutral-400 mb-1.5 block">Password</label>
          <div className="flex items-center gap-2 bg-black/40 border border-neutral-700 rounded-lg px-3 focus-within:border-[#73bf44] transition-colors mb-5">
            <LockKey size={16} className="text-neutral-500" />
            <input
              data-testid="dealer-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              placeholder="••••••••"
              className="w-full bg-transparent py-2.5 text-sm text-white outline-none placeholder:text-neutral-600"
            />
          </div>

          {error && (
            <div data-testid="dealer-login-error" className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2.5 mb-4">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !username.trim() || !password}
            data-testid="dealer-login-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-[#73bf44] text-[#050505] rounded-full font-heading font-bold py-3 hover:bg-[#5fa338] transition-colors disabled:opacity-60"
          >
            {loading ? <CircleNotch size={18} className="animate-spin" /> : null}
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>

        <button
          onClick={() => navigate("/claim-status")}
          data-testid="dealer-login-back"
          className="mt-5 w-full flex items-center justify-center gap-2 text-sm text-neutral-500 hover:text-white transition-colors"
        >
          <ArrowLeft size={14} /> Back to Track my Claim
        </button>
      </div>
    </div>
  );
}
